import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import {
  AccountEditPayload,
  AvailableAccountResponse,
  Connection,
  Session,
  TrackingCategory,
} from '@app/modules/businesses/models/accounting.interface';

import { Owner, ReservationPreview, Supplier } from '@app/modules/businesses/models/statements.models';
import { Business } from '@app/modules/my-business/interfaces/business.interface';
import { environment } from '@env/environment';
import { map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AccountingApiService {
  private readonly http = inject(HttpClient);

  #apiBase = `${environment.apiUrl}/operations/v2`;
  #businessesBase = `${environment.apiUrl}/businesses`;

  public createAccountingSession() {
    return this.http.post<Session>(`${this.#apiBase}/accounting/create`, {});
  }

  public deleteAccountingConnection(id: Connection['id']) {
    return this.http.delete(`${this.#apiBase}/accounting/connections/${id}`);
  }

  // Owner statement configured connections (business connections are returned on the business endpoint)
  public getAccountingConnections() {
    return this.http.get<Connection[]>(`${this.#apiBase}/accounting/connections`);
  }

  public getAccountingConnection(connectionId: Connection['id']) {
    return this.http.get<Connection>(`${this.#apiBase}/accounting/connections/${connectionId}`);
  }

  /**
   * Get available tracking categories for a connection
   */
  public getTrackingCategories(id: number) {
    return this.http.get<TrackingCategory[]>(`${this.#apiBase}/accounting/${id}/available-tracking-categories`);
  }

  /**
   * Get available supplier accounts for a connection
   */
  public getSupplierAccounts(id: number) {
    return this.http.get<Supplier[]>(`${this.#apiBase}/accounting/${id}/suppliers`);
  }

  /**
   * Get the current account mapping configuration for an existing connection
   */
  public getAccountingConnectionConfig(id: number) {
    return this.http.get<any>(`${this.#apiBase}/accounting/${id}/account-mapping`);
  }

  public saveAccountingConnectionConfig(id: number, data: AccountEditPayload) {
    return this.http.post<any>(`${this.#apiBase}/accounting/${id}/account-mapping`, data);
  }

  public saveAccountingConnectionSync(id: Connection['id'], data: { sync_start_date: string }) {
    return this.http.put<Connection>(`${this.#apiBase}/accounting/connections/${id}/sync-preferences`, data);
  }

  public refreshAccountingConnection(id: Connection['id']) {
    return this.http.put<any>(`${this.#apiBase}/accounting/connections/${id}/refresh`, {});
  }

  // Business specific endpoints
  public createBusinessAccountingSession(businessUuid: Business['uuid']) {
    return this.http.post<Session>(`${this.#businessesBase}/${businessUuid}/connections`, {});
  }

  /**
   * Get available accounts for a business
   */
  public getBusinessAccountOptions(businessUuid: Business['uuid']) {
    return this.http.get<AvailableAccountResponse>(
      `${this.#businessesBase}/${businessUuid}/connections/available-accounts`
    );
  }

  /**
   * Get reservations for a business, used to provide a list of reservations to preview the account mapping configuration
   */
  public getBusinessReservations(businessUuid: Business['uuid']) {
    return this.http.get<any>(`${this.#businessesBase}/${businessUuid}/reservations`).pipe(map(({ data }) => data));
  }

  /**
   * Preview the account mapping configuration while editing a connection
   */
  public previewBusinessAccounting(
    businessUuid: Business['uuid'],
    connectionId: Connection['id'],
    id: number,
    data: Partial<AccountEditPayload>
  ) {
    return this.http.post<{
      paid_journals: ReservationPreview;
      unpaid_journals: ReservationPreview;
    }>(`${this.#businessesBase}/${businessUuid}/account-mapping/${connectionId}/${id}/preview`, data);
  }

  public getBusinessAccountingOwners(businessUuid: Business['uuid']) {
    return this.http
      .get<{ data: Owner[] }>(`${this.#businessesBase}/${businessUuid}/owners`)
      .pipe(map(({ data }) => data));
  }

  public saveBusinessAccountingConnectionConfig(id: number, businessUuid: Business['uuid'], data: AccountEditPayload) {
    return this.http.post<any>(`${this.#businessesBase}/${businessUuid}/accounting/${id}/account-mapping`, data);
  }
}
